import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import styles from './AuthModal.module.scss';

import advLogo from '@/assets/img/advLogo.png';
import { AuthApi } from '@/entities/auth/api';
import { resolveSocialOAuthRedirectUrl } from '@/entities/auth/model/socialOAuthTypes';
import { companyStore } from '@/entities/company/store/companyStore';
import { restaurantStore } from '@/entities/restaurant/store/restaurantStore';
import { AuthService } from '@/shared/api';
import { imageLink } from '@/shared/lib';
import { normalizePhone } from '@/shared/lib/formatPhone';
import { Button } from '@/shared/ui-kit/Button/Button';
import { Icon } from '@/shared/ui-kit/Icon';
import { InputPhone } from '@/shared/ui-kit/InputPhone/InputPhone';
import { Modal } from '@/shared/ui-kit/Modal/Modal';
import { useToast } from '@/shared/ui-kit/ToastProvider/useToast';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
}

const enum Steps {
  'First' = 'First',
  'Second' = 'Second',
  'Third' = 'Third',
}

const IS_MOCKED = true;

export const AuthModal: React.FC<AuthModalProps> = ({ open, onClose }) => {
  const [value, setValue] = useState('+7 ');
  const [isSubmitted, setSubmitted] = useState(false);
  const [step, setStep] = useState(Steps.First);
  const [sendingSms, setSendingSms] = useState(false);
  const [socialAuthBusy, setSocialAuthBusy] = useState(false);

  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const [code, setCode] = useState(['', '', '', '']);

  const { showError, showSuccess } = useToast();

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const restaurant = restaurantStore.current.value;
  const logoUrl = restaurant?.image ? imageLink(restaurant.image) : null;

  const isValidPhone = useMemo(() => !!normalizePhone(value), [value]);

  const showInputError = isSubmitted && !isValidPhone;

  const checkedValidCode = code.join('');

  const showInputsErrors = code.every(Boolean) && checkedValidCode !== '1111';

  const handleClose = () => {
    setStep(Steps.First);
    onClose();
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault?.();
    setSubmitted(true);

    if (!value || value.trim() === '' || value.trim() === '+7') {
      showError('Укажите номер телефона');
      return false;
    }

    if (!isValidPhone) {
      showError('Введите корректный номер телефона');
      return false;
    }

    try {
      setSendingSms(true);

      showSuccess('СМС с кодом отправлено');

      return true;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Не удалось отправить СМС';

      showError(message);

      return false;
    } finally {
      setSendingSms(false);
    }
  };

  const handleSocialAuth = async (provider: 'yandex' | 'vk') => {
    try {
      setSocialAuthBusy(true);
      const start = await AuthApi.fetchSocialOAuthStart(provider);
      const url = resolveSocialOAuthRedirectUrl(start);
      window.location.href = url;
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Не удалось начать вход через соцсеть';
      showError(message);
    } finally {
      setSocialAuthBusy(false);
    }
  };

  const handleAuthNum = async () => {
    if (step === Steps.First) {
      setStep(Steps.Second);
      setSubmitted(false);

      if (!value || value.trim() === '') {
        setValue('+7 ');
      }

      return;
    }

    if (step === Steps.Second) {
      const success = await handleSubmit();

      if (success) {
        setStep(Steps.Third);
      }
    }
  };

  const handleCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const inputValue = e.target.value.slice(-1);

    const newCode = [...code];
    newCode[index] = inputValue;

    setCode(newCode);

    if (inputValue && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    const isFilled = newCode.every(Boolean);

    if (isFilled) {
      inputRefs.current[index]?.blur();
    }
  };

  const handleResendCode = async () => {
    try {
      setSendingSms(true);

      showSuccess('Код отправлен повторно');

      setTimer(60);
      setCanResend(false);

      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setCanResend(true);
            return 0;
          }

          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Не удалось отправить код';

      showError(message);
    } finally {
      setSendingSms(false);
    }
  };

  useEffect(() => {
    if (step !== Steps.Third) return;

    setTimer(60);
    setCanResend(false);

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [step]);

  const handlePrevStep = () => {
    if (step === Steps.Second) {
      setStep(Steps.First);
      setValue('+7 ');
    } else if (step === Steps.Third) {
      setStep(Steps.Second);
      setCode(['', '', '', '']);
    }
  };

  const isCodeFilled = code.every((item) => item !== '');

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  useEffect(() => {
    const codeIsFilled = code.every(Boolean);

    if (!codeIsFilled) {
      return;
    }

    if (checkedValidCode !== '1111') {
      showError('Неверный код из СМС');
    } else {
      showSuccess('Правильный код из СМС');
    }
    // Тут буду дергать метод для отправки кода на бэк
  }, [code, checkedValidCode, showError, showSuccess]);

  const handleYandexAuth = async () => {
    try {
      setSocialAuthBusy(true);

      const state = crypto.randomUUID();

      localStorage.setItem('yandex_oauth_state', state);

      localStorage.setItem(
        'post_auth_redirect',
        window.location.pathname + window.location.search
      );

      const response = await AuthService.v1AuthYandexLoginRetrieve({
        state,
      });

      window.location.href = response.redirect_url;
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Не удалось начать вход через Яндекс';

      showError(message);
    } finally {
      setSocialAuthBusy(false);
    }
  };

  return (
    <>
      <Modal
        fullScreen
        open={open}
        onClose={handleClose}
        hideHeader
        bgVariant="white"
        contentClassName={styles.modalContent}
        prevStep={step !== Steps.First}
        onPrev={handlePrevStep}
      >
        <div>
          <div className={styles.header}>
            <h1 className={styles.title}>
              {step !== Steps.First
                ? 'Войти по номеру телефона'
                : 'Вход или регистрация'}
            </h1>
          </div>

          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.centerBlock}>
              {logoUrl ? (
                <div className={styles.restaurantLogo}>
                  <img src={logoUrl} alt="logo" />
                </div>
              ) : (
                <div className={styles.centerBlock} />
              )}
              <div className={styles.authInfoBlock}>
                <h1 className={styles.orderType}>Order2go.ru</h1>
                <div className={styles.advProject}>
                  <img className={styles.advLogo} src={advLogo} alt="" />
                  <span className={styles.advText}>проект ADV</span>
                </div>
              </div>
            </div>

            <div className={styles.loginContainer}>
              {/* <InputPhone
                value={value}
                onChange={setValue}
                error={showInputError}
                isValid={isValidPhone}
              /> */}
              {step === Steps.First ? (
                <>
                  <div className={styles.btnsAuth}>
                    <Button
                      className={classNames(styles.button, styles.buttonYa)}
                      onClick={handleYandexAuth}
                      disabled={socialAuthBusy}
                    >
                      <Icon name="outlinedYa" /> Яндекс ID
                    </Button>
                    {!IS_MOCKED && (
                      <Button
                        className={classNames(styles.button, styles.buttonVk)}
                        onClick={() => handleSocialAuth('vk')}
                        disabled={socialAuthBusy}
                      >
                        <Icon name="outlinedVk" /> VK ID
                      </Button>
                    )}
                  </div>
                  {!IS_MOCKED && (
                    <div className={styles.blockSeparators}>
                      <div className={styles.separator} />
                      <p className={styles.descriptionSeparator}>или</p>
                      <div className={styles.separator} />
                    </div>
                  )}
                </>
              ) : (
                <InputPhone
                  value={value}
                  onChange={setValue}
                  error={showInputError}
                  isValid={isValidPhone}
                />
              )}
              {step === Steps.Third ? (
                <div className={styles.generalBlockThirdStep}>
                  <div className={styles.insideBlocks}>
                    <p className={styles.descriptionSms}>Код из СМС</p>
                    <div className={styles.inputsBlock}>
                      {[0, 1, 2, 3].map((item, index) => (
                        <input
                          key={item}
                          ref={(el) => {
                            inputRefs.current[index] = el;
                          }}
                          className={classNames(styles.inputStyles, {
                            [styles.allInputCorrect]: isCodeFilled,
                            [styles.errorStyles]: showInputsErrors,
                          })}
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={1}
                          value={code[index]}
                          onChange={(e) => handleCodeChange(e, index)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                        />
                      ))}
                    </div>
                  </div>
                  {canResend ? (
                    <Button
                      type="button"
                      className={styles.loginBtn}
                      onClick={handleResendCode}
                      disabled={sendingSms}
                    >
                      Отправить код еще раз
                    </Button>
                  ) : (
                    <p className={styles.timerCount}>
                      Отправить еще раз 0:{timer < 10 ? `0${timer}` : timer}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  {!IS_MOCKED && (
                    <Button
                      className={styles.loginBtn}
                      onClick={handleAuthNum}
                      disabled={sendingSms}
                    >
                      {step === Steps.First
                        ? 'Войти по номеру телефона'
                        : 'Получить код в СМС'}
                    </Button>
                  )}
                </>
              )}

              {/* <h2 className={styles.loginDescription}>
                Убедитесь, что вы авторизованы в Telegram по этому же номеру
              </h2> */}
            </div>
          </form>
        </div>
        {step === Steps.First && (
          <div className={styles.legal}>
            Продолжая регистрацию я соглашаюсь с{' '}
            <a
              href="/documents/Пользовательское_соглашение_публичная_оферта_для_сайта_АДВ_PARTNER.pdf"
              target="_blank"
            >
              Пользовательским соглашением
            </a>{' '}
            и{' '}
            <a
              href={companyStore.offerDocument.value?.file ?? '#'}
              target="_blank"
              rel="noreferrer"
            >
              Условиями Публичной оферты ИП Оганисян Артём Тигранович
            </a>
          </div>
        )}
      </Modal>
    </>
  );
};
